import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import userService from '../services/userService';
import UserModal from '../components/UserModal';
import useConfirm from '../hooks/useConfirm';
import RefreshButton from '../components/RefreshButton';
import { useTranslation } from 'react-i18next';
import './Users.css';

const Users = () => {
  const { t } = useTranslation();
  const { confirm, DialogComponent } = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser, isAdmin, isSuperAdmin } = useContext(AuthContext);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      message: t('users.confirm_delete', 'Tem certeza que deseja excluir este usuário?'),
      type: 'warning',
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await userService.deleteUser(id);
      setSuccess(t('users.deleted_success', 'Usuário excluído com sucesso!'));
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleModalSave = () => {
    loadUsers();
    handleModalClose();
    setSuccess(editingUser ? t('users.updated_success', 'Usuário atualizado com sucesso!') : t('users.created_success', 'Usuário criado com sucesso!'));
    setTimeout(() => setSuccess(''), 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
  return (
    <Layout>
      <DialogComponent />
      <div className="container">
          <div className="card">
            <h1>{t('common.access_denied', 'Acesso Negado')}</h1>
            <p>{t('common.no_permission', 'Você não tem permissão para acessar esta página.')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="users-page">
        <div className="users-container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="users-toolbar">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <RefreshButton onRefresh={loadUsers} loading={loading} />
            <button onClick={handleCreate} className="btn btn-primary">
              {t('users.new_user', '+ Novo Usuário')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">{t('users.loading', 'Carregando usuários...')}</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('users.name', 'Nome')}</th>
                  <th>{t('users.email', 'Email')}</th>
                  <th>{t('users.access_level', 'Nível de Acesso')}</th>
                  <th>{t('common.status', 'Status')}</th>
                  {isSuperAdmin && <th>{t('users.created_by', 'Criado por')}</th>}
                  <th>{t('users.created_at', 'Criado em')}</th>
                  <th>{t('common.actions', 'Ações')}</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 8 : 7} className="text-center">
                      {t('users.no_users', 'Nenhum usuário encontrado')}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge badge-${user.user_type === 'super_admin' ? 'super-admin' : user.role}`}>
                          {user.user_type === 'super_admin'
                            ? t('users.super_admin', 'Super Administrador')
                            : user.user_type === 'admin'
                            ? t('users.admin', 'Administrador')
                            : t('users.user', 'Usuário')}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${user.active ? 'active' : 'inactive'}`}>
                          {user.active ? t('common.active', 'Ativo') : t('common.inactive', 'Inativo')}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {user.creator ? (
                            <span title={`ID: ${user.creator.id}`}>
                              {user.creator.name} ({user.creator.email})
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{t('users.system', 'Sistema')}</span>
                          )}
                        </td>
                      )}
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="actions">
                          <button
                            onClick={() => handleEdit(user)}
                            className="btn btn-sm btn-secondary"
                          >
                            {t('common.edit', 'Editar')}
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="btn btn-sm btn-danger"
                            disabled={user.id === currentUser?.id}
                          >
                            {t('common.delete', 'Excluir')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        </div>

        {isModalOpen && (
          <UserModal
            user={editingUser}
            onClose={handleModalClose}
            onSave={handleModalSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default Users;

